import {
  ACCESS_TOKEN_STORAGE_KEY,
  ASSISTANTS_QUERY_KEY,
  AUTOMATION_MARKETPLACE_QUERY_KEY,
  CHAT_FOLDER_LIST_QUERY_KEY,
  CURRENT_PROJECT_UUID_STORAGE_KEY,
  GENERATED_FILES_QUERY_KEY,
  LAST_USED_PROJECT_UUID_STORAGE_KEY,
  MARKETPLACE_ASSISTANTS_QUERY_KEY,
} from "@frontend-shared/constants";
import {
  getHasAnyProjects,
  isQueryKeyForProject,
  parseCallbackOutcome,
  resolveActiveProjectUuid,
} from "@frontend-shared/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ANALYTICS_QUERY_KEY } from "@/api/analytics/useAnalytics";
import { MESSAGES_QUERY_KEY } from "@/api/analytics/useGetMessages";
import { useLogout } from "@/api/auth/useLogout";
import { useChatSettings } from "@/api/chat/useChatSettings";
import { useGetEnabledIntegrations } from "@/api/integration/useGetEnabledIntegrations";
import { MODELS_QUERY_KEY } from "@/api/models/useModels";
import { useGetProjectPermissions } from "@/api/project/useGetProjectPermissions";
import { useGetProjects } from "@/api/project/useGetProjects";
import { useGetUser } from "@/api/user/useUser";
import { PATHS } from "@/constants/paths";
import { AuthContext } from "@/contexts/AuthContext";
import { useIsBillingDisabled } from "@/hooks/use-is-billing-disabled";
import { useOnPremise } from "@/hooks/use-on-premise";
import i18n from "@/i18n";
import { getSamlOrganizationUuid } from "@/lib/auth";
import { websocketService } from "@/lib/websocket";
import {
  DOCUMENT_THUMBNAIL_QUERY_KEY,
  PREVIEW_BLOB_QUERY_KEY,
} from "@/pages/chat/constants/file-preview";
import { resetChatAttachmentStore } from "@/pages/chat/hooks/useChatAttachmentPersistence";
import { resetChatDraftStore } from "@/pages/chat/hooks/useChatDraft";
import { resetChatPastedTextStore } from "@/pages/chat/hooks/useChatPastedTexts";

interface IApplyProjectSwitchProps {
  projectUuid: string;
  // NOTE: a forced switch (the stored project stopped being accessible) is not
  // a user action, so it treads more lightly than an explicit one — see the
  // navigate and flushSync notes in applyProjectSwitch.
  isForced: boolean;
}

// NOTE: A chat URL names one conversation, and that conversation belongs to the
// project we are leaving — so it cannot survive a switch. Every other route
// re-reads its data under the new project and renders it, which is why a forced
// switch leaves them alone instead of yanking the user to /chat.
const getIsChatDetailRoute = (pathname: string): boolean =>
  pathname.startsWith(`${PATHS.CHAT}/`);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();

  const { isBillingDisabled } = useIsBillingDisabled();
  const { isOnPremise } = useOnPremise();

  const [currentProjectUuid, setCurrentProjectUuid] = useState<string | null>(
    localStorage.getItem(CURRENT_PROJECT_UUID_STORAGE_KEY) || null,
  );

  const {
    data: user,
    isLoading: isAuthLoading,
    refetch: getUser,
  } = useGetUser();

  const {
    data: projects,
    isLoading: isProjectsLoading,
    dataUpdatedAt: projectsUpdatedAt,
  } = useGetProjects(!!user);

  // NOTE: When the active project was last chosen, so a project list fetched
  // BEFORE that choice is never used to judge it — see the guard in the
  // selection effect.
  const projectSelectedAtRef = useRef<number>(0);

  const hasAnyProjectsForPermissions = getHasAnyProjects(projects);

  const {
    data: projectPermissions = null,
    isLoading: isProjectPermissionsLoading,
  } = useGetProjectPermissions(!!user && hasAnyProjectsForPermissions);

  const { data: chatSettings, isLoading: isChatSettingsLoading } =
    useChatSettings(!!user);

  // NOTE: Prefetch enabled integrations to avoid blinking when opening the action menu dropdown
  // NOTE: Gate behind integration:read permission to prevent 403 spam for users without this permission
  const hasIntegrationReadPermission =
    projectPermissions?.integration?.includes("read") ?? false;
  const { isLoading: isEnabledIntegrationsLoading } = useGetEnabledIntegrations(
    !!user && hasAnyProjectsForPermissions && hasIntegrationReadPermission,
  );

  const { mutateAsync: signOutMutation, isPending: isSigningOut } = useLogout();

  useEffect(() => {
    if (localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)) {
      getUser();
    }
  }, [getUser]);

  // NOTE: Listen for storage changes (for logout from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === null || e.key === ACCESS_TOKEN_STORAGE_KEY) {
        // NOTE: A cross-tab logout clears the token (newValue === null) or the
        // whole store (key === null). Drop the module-level chat stores too so
        // this tab can't hydrate the previous user's drafts/attachments after a
        // later re-auth (mirrors the same-tab signOut clear). Idempotent. Not
        // done on a token *refresh* (newValue is a fresh token), which would
        // otherwise wipe another tab's in-progress draft.
        if (e.key === null || e.newValue === null) {
          resetChatDraftStore();
          resetChatAttachmentStore();
          resetChatPastedTextStore();
        }
        getUser();
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    return () => globalThis.removeEventListener("storage", handleStorageChange);
  }, [getUser]);

  useEffect(() => {
    const handleLanguageChanged = (): void => {
      websocketService.reconnect();
    };
    i18n.on("languageChanged", handleLanguageChanged);
    return (): void => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  // NOTE: Handle error and success from url params.
  // NOTE: NSAI-6112 — these params are NOT server-sourced: anyone can put any
  // value in a link's query string. Only an allowlisted `messageKey` code picks
  // the wording (resolved in the user's UI language); an unknown code, or the
  // legacy free-text `message`, renders the generic message and is never shown.
  useEffect(() => {
    const outcome = parseCallbackOutcome({
      query: Object.fromEntries(searchParams),
    });

    // NOTE: `isNewIntegration` rides along on a native integration callback
    // (google.service.ts). It is not read anywhere, and leaving it behind is
    // not harmless: the page that started the connect sends its own URL as
    // `redirectTo`, so the leftover param comes back on the NEXT connect —
    // where it turned the Composio callback's hand-built "?success=true" into a
    // second "?" and swallowed the outcome. Cleared with the rest.
    // NOTE: `replace` so the browser Back button cannot return to the
    // pre-cleanup URL and re-fire this toast.
    const clearCallbackParams = (...params: string[]): void => {
      for (const param of [...params, "isNewIntegration"]) {
        searchParams.delete(param);
      }
      setSearchParams(searchParams, { replace: true });
    };

    // NOTE: NSAI-6112 — a bare `?error=true` (no message) shows nothing, but
    // it must still be cleared: this page's URL is the `redirectTo` of the next
    // connect, so a leftover flag would ride back next to the server's real
    // `success=true` and, error taking precedence, turn a success into a
    // failure. Only the literal "true" flag counts, so the SSO screen's own
    // `error=<code>` is left for it to read.
    const hasCallbackFlag =
      searchParams.get("error") === "true" ||
      searchParams.get("success") === "true";

    if (outcome) {
      const text = i18n.t(outcome.messageKey);

      if (outcome.isSuccess) {
        toast.success(text);
      } else {
        toast.error(text);
      }
    }

    if (outcome || hasCallbackFlag) {
      clearCallbackParams("error", "success", "message", "messageKey");
    }
  }, [searchParams, setSearchParams]);

  // NOTE: Redirect to select-plan page if user has no active subscription
  useEffect(() => {
    if (!user) return;

    const currentPath = globalThis.location.pathname;
    const allowedWithoutSubscription = [PATHS.SELECT_PLAN, PATHS.NO_PROJECTS];
    const isAllowedPath = allowedWithoutSubscription.some((path) =>
      currentPath.startsWith(path),
    );

    if (isBillingDisabled && currentPath.startsWith(PATHS.SELECT_PLAN)) {
      navigate(PATHS.CHAT, { replace: true });
      return;
    }

    if (
      !isBillingDisabled &&
      !isOnPremise &&
      !user.activeSubscription &&
      !isAllowedPath
    ) {
      navigate(PATHS.SELECT_PLAN);
    }
  }, [user, navigate, isBillingDisabled, isOnPremise]);

  // NOTE: Drops every project-scoped cache entry so nothing from the previous
  // project survives a switch. Shared by the explicit switch
  // (onCurrentProjectChange) and the forced one below, which re-points the app
  // when the stored project stops being accessible.
  const removeProjectScopedQueries = useCallback((): void => {
    // NOTE: Remove all chat-related queries before switching projects
    // to prevent loading chats from the previous project
    queryClient.removeQueries({ queryKey: ["chat-list"] });
    queryClient.removeQueries({ queryKey: ["chat-history"] });
    queryClient.removeQueries({ queryKey: ["chat"] });
    queryClient.removeQueries({ queryKey: [CHAT_FOLDER_LIST_QUERY_KEY] });

    // NOTE: Remove project-scoped models and assistants so the next read triggers
    // a fresh load (isLoading) and the picker shows loading only on project change
    queryClient.removeQueries({ queryKey: [MODELS_QUERY_KEY] });
    queryClient.removeQueries({ queryKey: [ASSISTANTS_QUERY_KEY] });
    queryClient.removeQueries({
      queryKey: [MARKETPLACE_ASSISTANTS_QUERY_KEY],
    });
    // NOTE: NSAI-5272 — same reasoning for the automation marketplace: the
    // listings are project-scoped, so the next project must not briefly show
    // the previous project's catalogue while its own request is in flight.
    queryClient.removeQueries({
      queryKey: [AUTOMATION_MARKETPLACE_QUERY_KEY],
    });

    // NOTE: Remove analytics queries — without this, the broad invalidate
    // in onCurrentProjectChange would synchronously refire each still-active
    // analytics fetch (DashboardTab/MessagesTab haven't unmounted yet) using
    // closures built from the OLD project's permissions, while the request is
    // already sent with the NEW project's X-Project-Uuid header.
    // `removeQueries` cancels in-flight requests and drops the cache so
    // no stale-context refetch fires.
    queryClient.removeQueries({ queryKey: [ANALYTICS_QUERY_KEY] });
    queryClient.removeQueries({ queryKey: [MESSAGES_QUERY_KEY] });
    // NOTE: NSAI-6501 — AI Generated Files lists, both tabs: the key carries
    // the tab and the filters but not the project, so the project just left
    // would otherwise show as placeholder data while the new one loads.
    queryClient.removeQueries({ queryKey: [GENERATED_FILES_QUERY_KEY] });

    // NOTE: NSAI-4045 — drop cached document-preview blobs/thumbnails too.
    // fileUuids are globally unique so cross-project collisions can't
    // happen, but file access is project-scoped (X-Project-Uuid), so keeping
    // another project's bytes in memory after a switch is not worth it.
    queryClient.removeQueries({ queryKey: [PREVIEW_BLOB_QUERY_KEY] });
    queryClient.removeQueries({ queryKey: [DOCUMENT_THUMBNAIL_QUERY_KEY] });
  }, [queryClient]);

  // NOTE: The full switch lifecycle, shared by the explicit switch and the
  // forced one (NSAI-4865). Persisting the uuid is not enough on its own: the
  // websocket stays joined to the project we left, the current route can be a
  // chat that belongs to it, and every non-chat query still holds its data —
  // so REST would talk to the new project while realtime, route and cache
  // still pointed at the old one.
  const applyProjectSwitch = useCallback(
    ({ projectUuid, isForced }: IApplyProjectSwitchProps): void => {
      projectSelectedAtRef.current = Date.now();
      const previousProjectUuid = localStorage.getItem(
        CURRENT_PROJECT_UUID_STORAGE_KEY,
      );
      removeProjectScopedQueries();

      localStorage.setItem(CURRENT_PROJECT_UUID_STORAGE_KEY, projectUuid);
      // NOTE: Also persist the durable last-used pointer (survives logout) so
      // the next login restores this project instead of the default org.
      localStorage.setItem(LAST_USED_PROJECT_UUID_STORAGE_KEY, projectUuid);
      setCurrentProjectUuid(projectUuid);
      websocketService.reconnect();

      // NOTE: An explicit switch always lands on a fresh chat in the new
      // project — that is what the user asked for. A forced one only moves the
      // route when the current one cannot survive the switch (a chat URL naming
      // a conversation in the project just lost); otherwise it would clobber
      // wherever the user actually is, including a destination another caller
      // is mid-navigation to (NotificationCard deep-links switch first, then
      // navigate).
      const shouldNavigate =
        !isForced || getIsChatDetailRoute(globalThis.location.pathname);

      if (shouldNavigate) {
        // NOTE: replace — overwrites the current history entry so the Back button
        // cannot return to a chat URL belonging to the previous project.
        // flushSync — forces React to re-render synchronously before navigate()
        // returns, ensuring isSwitchingProjectRef in useChatWebSocket is set to
        // true before any queued chatCreated WebSocket macrotask fires. Without
        // this, a chatCreated event that arrived while the message was in-flight
        // could navigate the user back to the old project's chat URL. It is off
        // for the forced path, which already runs inside an effect.
        navigate(PATHS.CHAT, { replace: true, flushSync: !isForced });
      }

      // NOTE: NSAI-4910 — queries keyed by the project we just left are marked
      // stale but NOT refetched: a refetch would go out under the new project's
      // header and store that response in the old project's slot, which would
      // then render when the user switches back. Their observers move to the
      // new project's key on the next render and fetch it there.
      queryClient.invalidateQueries({
        predicate: (query) =>
          isQueryKeyForProject({
            queryKey: query.queryKey,
            projectUuid: previousProjectUuid,
          }),
        refetchType: "none",
      });

      // NOTE: Invalidate other queries (models, integrations, etc.)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0] as string;
          return (
            !key.startsWith("chat") &&
            !isQueryKeyForProject({
              queryKey: query.queryKey,
              projectUuid: previousProjectUuid,
            })
          );
        },
      });
    },
    [navigate, queryClient, removeProjectScopedQueries],
  );

  useEffect(() => {
    if (!projects) {
      return;
    }

    const hasPersonalProject = !!projects.personalProject;
    const hasOrganizationProjects = projects.organizationProjects.some(
      (org) => org.projects.length > 0,
    );

    // NOTE: Redirect to no-projects page if user has no projects
    if (!hasPersonalProject && !hasOrganizationProjects) {
      const currentPath = globalThis.location.pathname;
      // NOTE: Allow access only to no-projects and profile settings
      const allowedPaths = [PATHS.NO_PROJECTS, PATHS.SETTINGS];
      const isAllowedPath = allowedPaths.some((path) =>
        currentPath.startsWith(path),
      );

      if (!isAllowedPath) {
        navigate(PATHS.NO_PROJECTS);
      }
      return;
    }

    // NOTE: Never judge the active project against a list that was fetched
    // BEFORE it was chosen. Right after creating a project — or switching to
    // one the cache has not seen yet — the cached list still predates it, and
    // acting on that would bounce the user straight back out of the project
    // they just picked. A pointer restored from storage has no selection time
    // (0), so the first list still validates it, which is what catches the
    // de-provisioned case.
    if (
      currentProjectUuid &&
      projectsUpdatedAt <= projectSelectedAtRef.current
    ) {
      return;
    }

    // NOTE: Restore the last-used project (falling back to the default) so a
    // returning user lands in the org they left — keeping branding consistent.
    const lastUsedProjectUuid = localStorage.getItem(
      LAST_USED_PROJECT_UUID_STORAGE_KEY,
    );
    // NOTE: NSAI-4865 — resolved on EVERY project-list change, not only when no
    // project is active yet. Access can disappear mid-session (SAML
    // de-provisioning, an admin removing the member, a deleted project), and a
    // current_project_uuid still aimed at a project the user has lost rides on
    // every request as X-Project-Uuid; the API answers that with 401, which the
    // client treats as an expired session and signs the user out. Re-resolving
    // moves them to a project they still have instead.
    //
    // NOTE: NSAI-5021 — the SAML org is a landing preference, not a filter, so
    // an SSO user with memberships in several orgs still opens in the tenant
    // whose IdP they signed in through while keeping the others reachable. It
    // only steers the fallback: a still-accessible current project wins over it,
    // because re-landing an SSO user in their IdP org on every list refresh
    // would undo the switch they just made by hand.
    const nextProjectUuid = resolveActiveProjectUuid({
      projects,
      currentProjectUuid,
      lastUsedProjectUuid,
      preferredOrganizationUuid: getSamlOrganizationUuid(),
    });

    if (!nextProjectUuid || nextProjectUuid === currentProjectUuid) {
      return;
    }

    // NOTE: The first selection after login is not a switch — there is no
    // previous project whose realtime/route/cache state needs tearing down, and
    // navigating would fight a deep link the user arrived on. Persist and go.
    if (!currentProjectUuid) {
      projectSelectedAtRef.current = Date.now();
      localStorage.setItem(CURRENT_PROJECT_UUID_STORAGE_KEY, nextProjectUuid);
      localStorage.setItem(LAST_USED_PROJECT_UUID_STORAGE_KEY, nextProjectUuid);
      setCurrentProjectUuid(nextProjectUuid);
      return;
    }

    // NOTE: Losing access mid-session IS a switch, so it runs the same
    // lifecycle an explicit one does — websocket, route and caches included.
    applyProjectSwitch({ projectUuid: nextProjectUuid, isForced: true });
  }, [
    currentProjectUuid,
    projects,
    projectsUpdatedAt,
    navigate,
    applyProjectSwitch,
  ]);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await signOutMutation();
      setCurrentProjectUuid(null);
      // NOTE: Logout is a client-side navigate (no reload), so the module-level
      // chat draft/attachment stores would otherwise survive into the next
      // session. Clear them so one user's unsent drafts/files can't hydrate
      // another user's composer.
      resetChatDraftStore();
      resetChatAttachmentStore();
      resetChatPastedTextStore();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, [signOutMutation]);

  const onCurrentProjectChange = useCallback(
    (uuid: string): void => {
      if (uuid === currentProjectUuid) {
        return;
      }

      applyProjectSwitch({ projectUuid: uuid, isForced: false });
    },
    [currentProjectUuid, applyProjectSwitch],
  );

  const isLoading =
    isAuthLoading ||
    isSigningOut ||
    isProjectsLoading ||
    isChatSettingsLoading ||
    isProjectPermissionsLoading ||
    isEnabledIntegrationsLoading;

  const value = useMemo(
    () => ({
      isLoading,
      isPersonalProject: currentProjectUuid === projects?.personalProject?.uuid,
      chatSettings: chatSettings || null,
      projects: projects || null,
      user: user || null,
      signOut,
      getUser,
      currentProjectUuid,
      onCurrentProjectChange,
      projectPermissions,
    }),
    [
      isLoading,
      currentProjectUuid,
      projects,
      chatSettings,
      user,
      signOut,
      getUser,
      onCurrentProjectChange,
      projectPermissions,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
